import type { 
  FunctionGraphConfig, 
  ScatterGraphConfig, 
  BarGraphConfig, 
  LineGraphConfig,
  TimelineGraphConfig 
} from '../components/GraphRenderer';

type GraphRenderConfig = FunctionGraphConfig | ScatterGraphConfig | BarGraphConfig | LineGraphConfig | TimelineGraphConfig;

// Graph token format: ```graph:type
// Example: ```graph:function
// or: ```graph:scatter
export const GRAPH_TOKEN_REGEX = /```graph:(\w+)\n([\s\S]*?)\s*```/g;

export interface ParsedGraph {
  type: string;
  config: GraphRenderConfig;
  originalText: string;
  placeholder: string;
}

/**
 * Parse graph blocks from markdown content
 */
export function parseGraphBlocks(content: string): ParsedGraph[] {
  const graphs: ParsedGraph[] = [];
  let match;
  let index = 0;

  GRAPH_TOKEN_REGEX.lastIndex = 0; // Reset regex state
  
  while ((match = GRAPH_TOKEN_REGEX.exec(content)) !== null) {
    const [fullMatch, type, configText] = match;
    const placeholder = `{{GRAPH_${index}}}`;
    
    try {
      const config = parseGraphConfig(type, configText.trim());
      
      graphs.push({
        type,
        config,
        originalText: fullMatch,
        placeholder
      });
    } catch (error) {
      console.warn(`Failed to parse graph: ${error}`);
      // Keep the original text if parsing fails
      continue;
    }
    
    index++;
  }
  
  return graphs;
}

/**
 * Replace graph blocks with placeholders for rendering
 */
export function replaceGraphsWithPlaceholders(content: string, graphs: ParsedGraph[]): string {
  let result = content;
  
  graphs.forEach(graph => {
    result = result.replace(graph.originalText, graph.placeholder);
  });
  
  return result;
}

/**
 * Process a key-value pair for graph configuration
 */
function processKeyValue(config: any, key: string, value: string, type: string): void {
  const trimmedKey = key.trim();
  const trimmedValue = value.trim();
  
  switch (trimmedKey) {
    case 'title':
      config.title = unquote(trimmedValue);
      break;
    case 'xRange':
      config.xRange = parseRange(trimmedValue);
      break;
    case 'yRange':
      config.yRange = parseRange(trimmedValue);
      break;
    case 'unit':
      config.unit = unquote(trimmedValue);
      break;
    case 'events':
      try {
        config.events = JSON.parse(trimmedValue);
      } catch (e) {
        console.warn('Failed to parse events:', e);
        config.events = [];
      }
      break;
    case 'periods':
      try {
        config.periods = JSON.parse(trimmedValue);
      } catch (e) {
        console.warn('Failed to parse periods:', e);
        config.periods = [];
      }
      break;
    case 'markers':
      if (trimmedValue === '[]') {
        config.markers = [];
      } else {
        try {
          config.markers = JSON.parse(trimmedValue);
        } catch (e) {
          config.markers = parseMarkers(trimmedValue);
        }
      }
      break;
    case 'description':
      config.description = unquote(trimmedValue);
      break;
    case 'grid':
      config.grid = trimmedValue.toLowerCase() === 'true';
      break;
    case 'xLabel':
      config.xLabel = unquote(trimmedValue);
      break;
    case 'yLabel':
      config.yLabel = unquote(trimmedValue);
      break;
    default:
      // Store other properties as-is
      config[trimmedKey] = unquote(trimmedValue);
  }
}

/**
 * Parse graph configuration from text
 */
function parseGraphConfig(type: string, configText: string): GraphRenderConfig {
  const config: Record<string, any> = { type };
  const lines = configText.split('\n');

  let collectingKey: string | null = null;
  let buffer: string[] = [];
  let bracketBalance = 0;

  const flushBuffer = () => {
    if (!collectingKey) {
      return;
    }
    const value = buffer.join(' ');
    assignGraphValue(type, config, collectingKey, value);
    collectingKey = null;
    buffer = [];
    bracketBalance = 0;
  };

  const startCollecting = (key: string, value: string) => {
    collectingKey = key;
    buffer = [value];
    bracketBalance = calculateBracketBalance(value);
    if (bracketBalance <= 0) {
      flushBuffer();
    }
  };

  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line || line.startsWith('//') || line.startsWith('#')) {
      continue;
    }

    if (collectingKey) {
      buffer.push(line);
      bracketBalance += calculateBracketBalance(line);
      if (bracketBalance <= 0) {
        flushBuffer();
      }
      continue;
    }

    const colonIndex = line.indexOf(':');
    if (colonIndex === -1) {
      continue;
    }

    const key = line.substring(0, colonIndex).trim();
    const value = line.substring(colonIndex + 1).trim();

    if (shouldCollectValue(key, value, type)) {
      startCollecting(key, value);
    } else {
      assignGraphValue(type, config, key, value);
    }
  }

  if (collectingKey) {
    flushBuffer();
  }

  postProcessFunctionConfig(config);
  validateGraphConfig(type, config);
  return config as GraphRenderConfig;
}

function assignGraphValue(type: string, config: Record<string, any>, key: string, rawValue: string): void {
  const normalizedKey = key.trim();
  const value = rawValue.trim();

  const assignBoolean = (targetKey: string) => {
    config[targetKey] = value.toLowerCase() === 'true';
  };

  const assignNumeric = (targetKey: string) => {
    const num = Number(value);
    if (!Number.isNaN(num)) {
      config[targetKey] = num;
    }
  };

  switch (normalizedKey) {
    case 'title':
      config.title = unquote(value);
      return;
    case 'description':
      config.description = unquote(value);
      return;
    case 'xLabel':
      config.xLabel = unquote(value);
      return;
    case 'yLabel':
      config.yLabel = unquote(value);
      return;
    case 'unit':
      config.unit = unquote(value);
      return;
    case 'variable':
      config.variable = unquote(value);
      return;
    case 'color':
    case 'color2':
    case 'color3':
      config[normalizedKey] = unquote(value);
      return;
    case 'grid':
    case 'intersections':
    case 'showDiagonal':
    case 'trend':
      assignBoolean(normalizedKey);
      return;
    case 'width':
    case 'height':
      assignNumeric(normalizedKey);
      return;
    case 'xRange':
    case 'yRange':
      config[normalizedKey] = parseRange(value);
      return;
    case 'expression':
    case 'expression2':
    case 'expression3':
      config[normalizedKey] = unquote(value);
      return;
    case 'expressions':
      config.expressions = parseStringArray(value);
      return;
    case 'labels':
      config.labels = parseStringArray(value);
      return;
    case 'colors':
      config.colors = parseStringArray(value);
      return;
    case 'markers':
      config.markers = parseMarkers(value);
      return;
    case 'datasets':
      config.datasets = parseDatasets(value);
      return;
    case 'points':
      config.points = parsePoints(value);
      if (type === 'line') {
        config.data = config.points;
      }
      return;
    case 'lines':
      config.lines = parseLines(value);
      return;
    case 'events':
      config.events = parseEventsOrPeriods(value);
      return;
    case 'periods':
      config.periods = parseEventsOrPeriods(value);
      return;
    case 'data':
      if (type === 'bar') {
        config.data = parseBarData(value);
      } else if (type === 'line') {
        config.data = parsePoints(value);
      }
      return;
    default:
      config[normalizedKey] = unquote(value);
  }
}

function shouldCollectValue(key: string, value: string, type: string): boolean {
  const arrayKeys = new Set(['expressions', 'labels', 'colors', 'markers', 'points', 'lines', 'events', 'periods', 'datasets']);
  if (arrayKeys.has(key)) {
    return calculateBracketBalance(value) > 0;
  }

  if (key === 'data') {
    if (type === 'bar' || type === 'line') {
      return calculateBracketBalance(value) > 0;
    }
  }

  return false;
}

function calculateBracketBalance(input: string): number {
  const openSquare = (input.match(/\[/g) || []).length;
  const closeSquare = (input.match(/\]/g) || []).length;
  const openCurly = (input.match(/\{/g) || []).length;
  const closeCurly = (input.match(/\}/g) || []).length;
  return openSquare - closeSquare + openCurly - closeCurly;
}

function postProcessFunctionConfig(config: Record<string, any>): void {
  if (config.type !== 'function') {
    return;
  }

  const collectedExpressions: string[] = [];
  if (typeof config.expression === 'string' && config.expression.trim()) {
    collectedExpressions.push(config.expression.trim());
  }

  const legacyExpressionKeys = Object.keys(config)
    .filter(key => /^expression\d+$/.test(key) && key !== 'expression' && key !== 'expressions')
    .sort((a, b) => parseInt(a.replace('expression', ''), 10) - parseInt(b.replace('expression', ''), 10));

  legacyExpressionKeys.forEach(key => {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) {
      collectedExpressions.push(value.trim());
    }
  });

  if (collectedExpressions.length && !Array.isArray(config.expressions)) {
    config.expressions = collectedExpressions;
  }

  const collectedColors: string[] = [];
  if (typeof config.color === 'string' && config.color.trim()) {
    collectedColors.push(config.color.trim());
  }
  const legacyColorKeys = Object.keys(config)
    .filter(key => /^color\d+$/.test(key) && key !== 'color' && key !== 'colors')
    .sort((a, b) => parseInt(a.replace('color', ''), 10) - parseInt(b.replace('color', ''), 10));

  legacyColorKeys.forEach(key => {
    const value = config[key];
    if (typeof value === 'string' && value.trim()) {
      collectedColors.push(value.trim());
    }
  });

  if (collectedColors.length && !Array.isArray(config.colors)) {
    config.colors = collectedColors;
  }
}

/**
 * Validate graph configuration
 */
function validateGraphConfig(type: string, config: any): void {
  switch (type) {
    case 'function':
      // At least one expression format is required
      if (!config.expression && (!config.expressions || config.expressions.length === 0)) {
        throw new Error('Function graphs require at least one expression');
      }
      // Validate optional arrays
      if (config.expressions && !Array.isArray(config.expressions)) {
        throw new Error('Function graph expressions must be an array');
      }
      if (config.labels && !Array.isArray(config.labels)) {
        throw new Error('Function graph labels must be an array');
      }
      if (config.colors && !Array.isArray(config.colors)) {
        throw new Error('Function graph colors must be an array');
      }
      if (config.markers && !Array.isArray(config.markers)) {
        throw new Error('Function graph markers must be an array');
      }
      // Points and lines are optional for function graphs
      if (config.points && !Array.isArray(config.points)) {
        throw new Error('Function graph points must be an array');
      }
      if (config.lines && !Array.isArray(config.lines)) {
        throw new Error('Function graph lines must be an array');
      }
      break;
    case 'scatter':
      {
        const hasDatasets = Array.isArray(config.datasets) && config.datasets.length > 0;
        const hasPoints = Array.isArray(config.points) && config.points.length > 0;
        if (!hasDatasets && !hasPoints) {
          throw new Error('Scatter plots require points data');
        }
      }
      break;
    case 'bar':
      if (!config.data || typeof config.data !== 'object' || Object.keys(config.data).length === 0) {
        throw new Error('Bar charts require data object');
      }
      break;
    case 'line':
      if (!config.data || !Array.isArray(config.data) || config.data.length === 0) {
        throw new Error('Line graphs require data array');
      }
      break;
    case 'timeline':
      // Timeline graphs can have events, periods, or markers
      if (!config.events && !config.periods && !config.markers) {
        throw new Error('Timeline graphs require at least one of: events, periods, or markers');
      }
      break;
    default:
      throw new Error(`Unknown graph type: ${type}`);
  }
}

/**
 * Helper functions
 */
function unquote(str: string): string {
  if ((str.startsWith('"') && str.endsWith('"')) || 
      (str.startsWith("'") && str.endsWith("'"))) {
    return str.slice(1, -1);
  }
  return str;
}

function parseRange(str: string): [number, number] {
  const match = str.match(/\[([^,]+),\s*([^\]]+)\]/);
  if (!match) {
    throw new Error(`Invalid range format: ${str}. Use [min, max]`);
  }
  
  // Try to evaluate mathematical expressions safely
  const evaluateExpression = (expr: string): number => {
    // Remove whitespace
    expr = expr.trim();
    
    // If it's already a number, return it
    const num = parseFloat(expr);
    if (!isNaN(num) && expr === num.toString()) {
      return num;
    }
    
    // Replace Math constants and evaluate simple expressions
    let evaluated = expr
      .replace(/Math\.PI/g, Math.PI.toString())
      .replace(/Math\.E/g, Math.E.toString())
      .replace(/PI/g, Math.PI.toString())
      .replace(/E/g, Math.E.toString());
    
    // Evaluate multiplication
    const multiplyMatch = evaluated.match(/(-?\d+(?:\.\d+)?)\s*\*\s*(-?\d+(?:\.\d+)?)/);
    if (multiplyMatch) {
      const result = parseFloat(multiplyMatch[1]) * parseFloat(multiplyMatch[2]);
      evaluated = evaluated.replace(multiplyMatch[0], result.toString());
    }
    
    // Evaluate division
    const divideMatch = evaluated.match(/(-?\d+(?:\.\d+)?)\s*\/\s*(-?\d+(?:\.\d+)?)/);
    if (divideMatch) {
      const result = parseFloat(divideMatch[1]) / parseFloat(divideMatch[2]);
      evaluated = evaluated.replace(divideMatch[0], result.toString());
    }
    
    // Final parse
    const finalNum = parseFloat(evaluated);
    if (isNaN(finalNum)) {
      throw new Error(`Cannot evaluate expression: ${expr}`);
    }
    
    return finalNum;
  };
  
  try {
    return [evaluateExpression(match[1]), evaluateExpression(match[2])];
  } catch (error) {
    throw new Error(`Invalid range format: ${str}. Error: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

function stripJsonComments(str: string): string {
  return str.replace(/\/\/.*$/gm, '');
}

function parsePoints(str: string): [number, number][] {
  const trimmed = str.trim();
  if (!trimmed) {
    return [];
  }

  const normalizeBracketNotation = (value: string): string =>
    value.replace(/\(/g, '[').replace(/\)/g, ']').replace(/'/g, '"');

  const tryJsonParse = (value: string): [number, number][] | null => {
    try {
      const parsed = JSON.parse(normalizeBracketNotation(value));
      if (!Array.isArray(parsed)) {
        return null;
      }
      return parsed.map(entry => {
        if (!Array.isArray(entry) || entry.length !== 2) {
          throw new Error('Each point must contain exactly two numeric values');
        }
        const x = Number(entry[0]);
        const y = Number(entry[1]);
        if (!isFinite(x) || !isFinite(y)) {
          throw new Error('Point coordinates must be finite numbers');
        }
        return [x, y];
      });
    } catch {
      return null;
    }
  };

  const jsonParsed = tryJsonParse(trimmed);
  if (jsonParsed) {
    return jsonParsed;
  }

  try {
    // Legacy fallback: [(x1,y1), (x2,y2)]
    const cleaned = trimmed.replace(/[\[\]]/g, '');
    const pointStrings = cleaned.split(/[)]\s*,?\s*[(]/);

    return pointStrings
      .map(pointStr => {
        const coords = pointStr
          .replace(/[()]/g, '')
          .split(',')
          .map(s => s.trim())
          .filter(Boolean);
        if (coords.length !== 2) {
          throw new Error('Each point must have exactly 2 coordinates');
        }
        const x = Number(coords[0]);
        const y = Number(coords[1]);
        if (!isFinite(x) || !isFinite(y)) {
          throw new Error('Point coordinates must be finite numbers');
        }
        return [x, y] as [number, number];
      })
      .filter(Boolean);
  } catch (error) {
    throw new Error(`Invalid points format: ${str}. Use [(x1,y1), (x2,y2), ...]`);
  }
}

const LOOSE_JSON_KEY_REGEX = /(^|[\{,\[])(\s*)([A-Za-z_][\w]*)\s*:/g;

function normalizeLooseJson(str: string): string {
  return str.replace(LOOSE_JSON_KEY_REGEX, (_, prefix: string, whitespace: string, key: string) => {
    return `${prefix}${whitespace}"${key}":`;
  });
}

function parseDatasets(str: string): Array<{label?: string; color?: string; points: [number, number][]}> {
  const withoutComments = stripJsonComments(str).trim();
  if (!withoutComments || withoutComments === '[]') {
    return [];
  }

  const normalized = normalizeLooseJson(withoutComments).replace(/'/g, '"');

  let parsed: any;
  try {
    parsed = JSON.parse(normalized);
  } catch (error) {
    throw new Error(`Invalid datasets format: ${str}`);
  }

  if (!Array.isArray(parsed)) {
    throw new Error('Datasets must be an array');
  }

  return parsed.map((dataset, index) => {
    if (typeof dataset !== 'object' || dataset === null) {
      throw new Error(`Dataset at index ${index} must be an object`);
    }
    if (!dataset.points) {
      throw new Error(`Dataset at index ${index} is missing "points"`);
    }

    return {
      label: dataset.label !== undefined ? String(dataset.label) : undefined,
      color: dataset.color !== undefined ? String(dataset.color) : undefined,
      points: parsePoints(JSON.stringify(dataset.points))
    };
  });
}

function parseBarData(str: string): Record<string, number> {
  try {
    // Parse object-like syntax: {A: 10, B: 20, C: 15}
    const cleaned = str.replace(/[{}]/g, '');
    const pairs = cleaned.split(',');
    const result: Record<string, number> = {};
    
    for (const pair of pairs) {
      const [key, value] = pair.split(':').map(s => s.trim());
      if (!key || !value) continue;
      
      const cleanKey = unquote(key);
      const numValue = parseFloat(value);
      
      if (isNaN(numValue)) {
        throw new Error(`Invalid number value: ${value}`);
      }
      
      result[cleanKey] = numValue;
    }
    
    return result;
  } catch (error) {
    throw new Error(`Invalid bar data format: ${str}. Use {label1: value1, label2: value2, ...}`);
  }
}

function parseLines(str: string): Array<{type: 'vertical' | 'horizontal' | 'line', value?: number, points?: [number, number][], color?: string, style?: 'solid' | 'dashed' | 'dotted'}> {
  try {
    // Parse lines array: [vertical:100, horizontal:50, line:[(0,0),(100,150)]]
    const cleaned = str.replace(/[\[\]]/g, '');
    const lineStrings = cleaned.split(',').map(s => s.trim());
    const lines: Array<{type: 'vertical' | 'horizontal' | 'line', value?: number, points?: [number, number][], color?: string, style?: 'solid' | 'dashed' | 'dotted'}> = [];
    
    for (const lineStr of lineStrings) {
      if (!lineStr) continue;
      
      const [type, ...valueParts] = lineStr.split(':');
      const value = valueParts.join(':').trim();
      
      if (type === 'vertical' || type === 'horizontal') {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          lines.push({ type: type as 'vertical' | 'horizontal', value: numValue });
        }
      } else if (type === 'line') {
        const points = parsePoints(value);
        lines.push({ type: 'line', points });
      }
    }
    
    return lines;
  } catch (error) {
    throw new Error(`Invalid lines format: ${str}. Use [vertical:value, horizontal:value, line:[(x1,y1),(x2,y2)]]`);
  }
}

function parseStringArray(str: string): string[] {
  try {
    // Parse array format: ["item1", "item2", "item3"] or [item1, item2, item3]
    const cleaned = str.replace(/^\[|\]$/g, ''); // Remove outer brackets only
    
    // Smart splitting that respects parentheses and quotes
    const items: string[] = [];
    let current = '';
    let parenDepth = 0;
    let inQuotes = false;
    let quoteChar = '';
    
    for (let i = 0; i < cleaned.length; i++) {
      const char = cleaned[i];
      const prevChar = i > 0 ? cleaned[i - 1] : '';
      
      if ((char === '"' || char === "'") && prevChar !== '\\') {
        if (!inQuotes) {
          inQuotes = true;
          quoteChar = char;
        } else if (char === quoteChar) {
          inQuotes = false;
          quoteChar = '';
        }
      } else if (!inQuotes) {
        if (char === '(') {
          parenDepth++;
        } else if (char === ')') {
          parenDepth--;
        } else if (char === ',' && parenDepth === 0) {
          // This is a top-level comma, split here
          items.push(current.trim());
          current = '';
          continue;
        }
      }
      
      current += char;
    }
    
    // Add the last item
    if (current.trim()) {
      items.push(current.trim());
    }
    
    return items.map(item => unquote(item));
  } catch (error) {
    throw new Error(`Invalid string array format: ${str}. Use ["item1", "item2", ...]`);
  }
}

function parseMarkers(str: string): Array<{x: number, label: string}> {
  try {
    // Parse markers format: [{"x": 80, "label": "Y*"}, {"x": 120, "label": "YF"}]
    const cleaned = str.replace(/[\[\]]/g, '');
    const markerStrings = cleaned.split(/},\s*{/);
    const markers: Array<{x: number, label: string}> = [];
    
    for (let markerStr of markerStrings) {
      // Clean up the marker string
      markerStr = markerStr.replace(/[{}]/g, '');
      
      const parts = markerStr.split(',');
      let x: number | undefined;
      let label: string | undefined;
      
      for (const part of parts) {
        const [key, ...valueParts] = part.split(':');
        const value = valueParts.join(':').trim();
        
        if (key.trim() === '"x"' || key.trim() === 'x') {
          x = parseFloat(value);
        } else if (key.trim() === '"label"' || key.trim() === 'label') {
          label = unquote(value);
        }
      }
      
      if (x !== undefined && label !== undefined) {
        markers.push({x, label});
      }
    }
    
    return markers;
  } catch (error) {
    throw new Error(`Invalid markers format: ${str}. Use [{"x": value, "label": "text"}, ...]`);
  }
}

function parseEventsOrPeriods(str: string): Array<Record<string, any>> {
  try {
    const normalized = str.replace(/'/g, '"');
    const data = JSON.parse(normalized);
    if (!Array.isArray(data)) {
      throw new Error('Entries must be an array');
    }

    return data.map(entry => {
      if (typeof entry !== 'object' || entry === null) {
        throw new Error('Each entry must be an object');
      }
      const parsed: Record<string, any> = {};
      Object.entries(entry).forEach(([entryKey, entryValue]) => {
        if (['time', 'start', 'end', 'x'].includes(entryKey)) {
          const num = Number(entryValue);
          if (Number.isNaN(num)) {
            throw new Error(`Invalid numeric value for ${entryKey}`);
          }
          parsed[entryKey] = num;
        } else {
          parsed[entryKey] = entryValue;
        }
      });
      return parsed;
    });
  } catch (error) {
    throw new Error(`Invalid timeline array format: ${str}`);
  }
}

/**
 * Utility functions for creating common graph configurations
 */
export const graphTemplates = {
  function: {
    linear: (slope: number = 1, intercept: number = 0): string => `
title: "Linear Function: y = ${slope}x + ${intercept}"
expression: "${slope}*x + ${intercept}"
grid: true
xRange: [-10, 10]
yRange: [-10, 10]
xLabel: "x"
yLabel: "f(x)"`,

    quadratic: (a: number = 1, b: number = 0, c: number = 0): string => `
title: "Quadratic Function: y = ${a}x² + ${b}x + ${c}"
expression: "${a}*x*x + ${b}*x + ${c}"
grid: true
xRange: [-10, 10]
yRange: [-10, 10]
xLabel: "x"
yLabel: "f(x)"`,

    sine: (amplitude: number = 1, frequency: number = 1): string => `
title: "Sine Wave: y = ${amplitude}sin(${frequency}x)"
expression: "${amplitude}*Math.sin(${frequency}*x)"
grid: true
xRange: [-6.28, 6.28]
yRange: [-${amplitude + 1}, ${amplitude + 1}]`,

    exponential: (base: number = Math.E): string => `
title: "Exponential Function: y = ${base}^x"
expression: "Math.pow(${base}, x)"
grid: true
xRange: [-5, 5]
yRange: [0, 20]`,

    multi: (expressions: string[], colors?: string[], title?: string): string => `
title: "${title || 'Multiple Functions'}"
${expressions.map((expr, i) => `expression${i === 0 ? '' : i + 1}: "${expr}"`).join('\n')}
${colors ? colors.map((color, i) => `color${i === 0 ? '' : i + 1}: "${color}"`).join('\n') : ''}
grid: true
xRange: [-10, 10]
yRange: [-10, 10]`
  },

  scatter: {
    basic: (points: [number, number][]): string => `
title: "Scatter Plot"
points: [${points.map(([x, y]) => `(${x},${y})`).join(', ')}]
grid: true
trend: false`,

    withTrend: (points: [number, number][]): string => `
title: "Scatter Plot with Trend Line"
points: [${points.map(([x, y]) => `(${x},${y})`).join(', ')}]
grid: true
trend: true`
  },

  bar: {
    basic: (data: Record<string, number>): string => `
title: "Bar Chart"
data: {${Object.entries(data).map(([k, v]) => `"${k}": ${v}`).join(', ')}}
grid: true`,
  },

  line: {
    basic: (points: [number, number][]): string => `
title: "Line Graph"
data: [${points.map(([x, y]) => `(${x},${y})`).join(', ')}]
grid: true`
  }
};

/**
 * Create example graphs for testing
 */
export const exampleGraphs = {
  function: `\`\`\`graph:function
title: "Quadratic Function"
expression: "x*x - 4*x + 3"
grid: true
color: "#3b82f6"
xRange: [-2, 6]
yRange: [-2, 8]
\`\`\``,

  multiFunction: `\`\`\`graph:function
title: "Multiple Functions"
expressions: ["x*x", "2*x + 1", "Math.sin(x)"]
labels: ["Quadratic", "Linear", "Sine"]
colors: ["#3b82f6", "#ef4444", "#10b981"]
grid: true
xRange: [-5, 5]
yRange: [-5, 15]
\`\`\``,

  economicsAD: `\`\`\`graph:function
title: "Aggregate Demand and Output Equilibrium"
expressions: ["x", "0.75*x + 20"]
labels: ["AD=Y", "AD₀"]
grid: true
colors: ["#8b5cf6", "#ef4444"]
xRange: [0, 150]
yRange: [0, 150]
xLabel: "National Product (Y)"
yLabel: "Aggregate Demand (AD)"
markers: [{"x": 80, "label": "Y*"}, {"x": 120, "label": "YF"}]
intersections: true
showDiagonal: true
description: "The graph shows the equilibrium where aggregate demand equals output (AD=Y intersection). The AD₀ curve represents autonomous spending plus induced consumption, intersecting the 45-degree line at equilibrium output Y*."
\`\`\``,

  scatter: `\`\`\`graph:scatter
title: "Sample Data Points"
points: [(1,2), (2,4), (3,5), (4,7), (5,8)]
grid: true
trend: true
color: "#ef4444"
\`\`\``,

  bar: `\`\`\`graph:bar
title: "Test Scores by Subject"
data: {"Math": 85, "Science": 92, "English": 78, "History": 88}
grid: true
color: "#10b981"
\`\`\``,

  line: `\`\`\`graph:line
title: "Temperature Over Time"
data: [(0,20), (1,22), (2,25), (3,23), (4,27), (5,30)]
grid: true
color: "#f59e0b"
xLabel: "Time (hours)"
yLabel: "Temperature (°C)"
\`\`\``
};

/**
 * Physics-specific graph templates
 */
export const physicsGraphs = {
  kinematics: {
    position: (v0: number, a: number): string => `
title: "Position vs Time: x = ${v0}t + ½(${a})t²"
expression: "${v0}*x + 0.5*${a}*x*x"
grid: true
xLabel: "Time (s)"
yLabel: "Position (m)"
xRange: [0, 10]
yRange: [0, 100]`,

    velocity: (v0: number, a: number): string => `
title: "Velocity vs Time: v = ${v0} + ${a}t"
expression: "${v0} + ${a}*x"
grid: true
xLabel: "Time (s)"
yLabel: "Velocity (m/s)"
xRange: [0, 10]
yRange: [-10, 50]`
  },

  waves: {
    sine: (amplitude: number, frequency: number): string => `
title: "Wave Function: y = ${amplitude}sin(${frequency}x)"
expression: "${amplitude}*Math.sin(${frequency}*x)"
grid: true
xLabel: "Position (m)"
yLabel: "Displacement (m)"
xRange: [0, 12.56]
yRange: [-${amplitude + 1}, ${amplitude + 1}]`
  }
};

/**
 * Chemistry-specific graph templates
 */
export const chemistryGraphs = {
  concentration: {
    firstOrder: (k: number, c0: number): string => `
title: "First-Order Decay: [A] = ${c0}e^(-${k}t)"
expression: "${c0}*Math.exp(-${k}*x)"
grid: true
xLabel: "Time"
yLabel: "Concentration"
xRange: [0, 5]
yRange: [0, ${c0 + 1}]`
  },

  equilibrium: (keq: number): string => `
title: "Equilibrium Constant Plot"
expression: "${keq}/(1 + x)"
grid: true
xLabel: "Reaction Progress"
yLabel: "Product Concentration"
xRange: [0, 10]`
};

export const economicsHebrew = `\`\`\`graph:function
title: "שוק הכסף: עליית ריבית עקב עליית מחירים"
expression: "0.05 * Math.pow(x, -1.5)"
expression2: "0.05 * Math.pow(x, -1.5) * 0.8"
grid: true
color: "#3b82f6"
color2: "#ef4444"
xRange: [0.1, 2]
yRange: [0.01, 1]
xLabel: "כמות כסף ריאלית (MS/P)"
yLabel: "ריבית (R)"
description: "עקומת הביקוש לכסף (MD) יורדת משמאל לימין. עקומת היצע הכסף הריאלי (MS/P) יורדת מ-MS/P המקורי (כחול) ל-MS/P החדש (אדום), כתוצאה מעליית המחירים. תזוזה זו גורמת לעליית הריבית מ-R המקורי ל-R החדש."
\`\`\``;

export const economicsWithLines = `\`\`\`graph:function
title: "שוק המוצרים: עלייה ב-G ופער אינפלציוני"
expression: "Math.min(x, 150)"
points: [(0,0), (100,100), (100,150)]
lines: [vertical:100]
grid: true
color: "#3b82f6"
xRange: [0, 150]
yRange: [0, 150]
xLabel: "תוצר (Y)"
yLabel: "ביקוש מצרפי (AD)"
description: "עקומת ה-AD (קו כחול) מייצגת את הביקוש המצרפי. הקו האנכי ב-Y_F=100 מייצג את התוצר בתעסוקה מלאה. עלייה ב-G מזיזה את AD ימינה ויוצרת פער אינפלציוני."
\`\`\``; 
